/**
 * SearchModeSelector
 * Shared component for selecting search mode (Recent/Affaire/Client) in facturation views
 * Client mode uses debounced live search with dropdown results (like search tab)
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  ClockIcon,
  FolderIcon,
  UserIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useClientSearch, type FacturationClient } from '../../hooks/useClientSearch';

export type SearchMode = 'recent' | 'affaire' | 'client' | 'facture';

interface SearchModeSelectorProps {
  mode: SearchMode;
  onModeChange: (mode: SearchMode) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSearch: () => void;
  onClientSelect?: (client: FacturationClient) => void;
  isLoading?: boolean;
}

const modeConfig: Record<SearchMode, { label: string; icon: React.ElementType; placeholder: string; description: string }> = {
  recent: {
    label: 'Recent',
    icon: ClockIcon,
    placeholder: '',
    description: '20 dernieres factures'
  },
  affaire: {
    label: 'Affaire',
    icon: FolderIcon,
    placeholder: 'Numero dossier ou ID affaire...',
    description: 'Rechercher par dossier'
  },
  client: {
    label: 'Client',
    icon: UserIcon,
    placeholder: 'Nom, prenom ou email du client...',
    description: 'Rechercher par client'
  },
  facture: {
    label: 'Facture',
    icon: DocumentTextIcon,
    placeholder: 'Numero (ex: 2026-00971) ou communication (ex: +++097/1070/78397+++)...',
    description: 'Par numero ou communication'
  }
};

export const SearchModeSelector: React.FC<SearchModeSelectorProps> = ({
  mode,
  onModeChange,
  searchTerm,
  onSearchChange,
  onSearch,
  onClientSelect,
  isLoading = false
}) => {
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Client search hook (only used when mode is 'client')
  const {
    clients,
    loading: clientLoading,
    error: clientError,
    searchQuery: clientSearchQuery,
    setSearchQuery: setClientSearchQuery,
    hasSearched: clientHasSearched,
    reset: resetClientSearch,
  } = useClientSearch();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset client search when mode changes
  useEffect(() => {
    if (mode !== 'client') {
      resetClientSearch();
      setShowClientDropdown(false);
    }
  }, [mode, resetClientSearch]);

  // Show dropdown when typing in client mode
  useEffect(() => {
    if (mode === 'client' && clientSearchQuery.length >= 2) {
      setShowClientDropdown(true);
    }
  }, [mode, clientSearchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((mode === 'affaire' || mode === 'facture') && searchTerm.trim()) {
      onSearch();
    }
  };

  const handleClientInputChange = (value: string) => {
    setClientSearchQuery(value);
    onSearchChange(value);
  };

  const handleClientSelect = (client: FacturationClient) => {
    setShowClientDropdown(false);
    onSearchChange(`${client.firstName} ${client.lastName}`);
    if (onClientSelect) {
      onClientSelect(client);
    }
    // Trigger search with client name
    onSearch();
  };

  const handleClearSearch = () => {
    if (mode === 'client') {
      resetClientSearch();
      onSearchChange('');
    } else {
      onSearchChange('');
    }
  };

  // Get initials for avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className="search-mode-selector space-y-6">
      {/* Step 1: Mode selection - Large cards */}
      <div className="fact-search-mode-cards">
        {(Object.keys(modeConfig) as SearchMode[]).map((modeKey) => {
          const config = modeConfig[modeKey];
          const Icon = config.icon;
          const isActive = mode === modeKey;

          return (
            <button
              key={modeKey}
              type="button"
              onClick={() => onModeChange(modeKey)}
              className={`fact-search-mode-card ${isActive ? 'fact-search-mode-card--active' : ''}`}
            >
              <div className="fact-search-mode-card__icon-wrapper">
                <Icon className="fact-search-mode-card__icon" />
              </div>
              <div className="fact-search-mode-card__content">
                <div className="fact-search-mode-card__label">{config.label}</div>
                <div className="fact-search-mode-card__description">
                  {config.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Step 2: Search input - Affaire mode (manual submit) */}
      {mode === 'affaire' && (
        <form onSubmit={handleSubmit} className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder={modeConfig[mode].placeholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
              className="w-full pl-12 pr-5 py-4 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !searchTerm.trim()}
            className="px-8 py-4 bg-primary text-white rounded-xl text-base font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Recherche...' : 'Rechercher'}
          </button>
        </form>
      )}

      {/* Step 2: Search input - Facture mode (manual submit) */}
      {mode === 'facture' && (
        <form onSubmit={handleSubmit} className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder={modeConfig[mode].placeholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
              className="w-full pl-12 pr-5 py-4 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !searchTerm.trim()}
            className="px-8 py-4 bg-primary text-white rounded-xl text-base font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Recherche...' : 'Rechercher'}
          </button>
        </form>
      )}

      {/* Step 2: Search input - Client mode (live search with dropdown) */}
      {mode === 'client' && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder={modeConfig[mode].placeholder}
              value={clientSearchQuery}
              onChange={(e) => handleClientInputChange(e.target.value)}
              onFocus={() => clientSearchQuery.length >= 2 && setShowClientDropdown(true)}
              autoFocus
              className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {clientSearchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Client search dropdown */}
          {showClientDropdown && (
            <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
              {/* Loading state */}
              {clientLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
                  <span className="ml-3 text-gray-500">Recherche en cours...</span>
                </div>
              )}

              {/* Error state */}
              {clientError && (
                <div className="px-4 py-3 text-red-600 bg-red-50">
                  {clientError}
                </div>
              )}

              {/* No results */}
              {clientHasSearched && !clientLoading && clients.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <UserIcon className="h-10 w-10 text-gray-300 mb-2" />
                  <p className="font-medium">Aucun client trouve</p>
                  <p className="text-sm">Essayez avec un autre nom ou email</p>
                </div>
              )}

              {/* Results list */}
              {clients.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Resultats</span>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                      {clients.length} client{clients.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="overflow-y-auto max-h-64">
                    {clients.map((client) => (
                      <button
                        key={client.id || client.fmClientId}
                        type="button"
                        onClick={() => handleClientSelect(client)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                      >
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-primary">
                            {getInitials(client.firstName, client.lastName)}
                          </span>
                        </div>
                        {/* Client info */}
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {client.firstName} {client.lastName}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {client.email || client.phone || 'Pas de contact'}
                          </div>
                        </div>
                        {/* Company badge */}
                        {client.company && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded truncate max-w-[120px]">
                            {client.company}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Initial state - waiting for input */}
              {!clientLoading && !clientHasSearched && clientSearchQuery.length < 2 && (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <MagnifyingGlassIcon className="h-10 w-10 text-gray-300 mb-2" />
                  <p className="text-sm">Tapez au moins 2 caracteres pour rechercher</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchModeSelector;
